import { useState } from "react";
import {
  Card, Table, Button, Tag, Select, Input, Modal, Form, DatePicker, Tooltip, message, Popconfirm,
} from "antd";
import { RefreshCcw, Download, CheckCircle, XCircle } from "lucide-react";
import { useDataExportList, useDataExportUpdateStatus } from "../../../api-hooks/dataExports";
import { TDataExport, TDataExportStatus } from "../../../types";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";

// GDPR Article 20 — companies request data exports for compliance.
// Super admin processes and provides download link.

const { Option } = Select;

const STATUS_COLOR: Record<TDataExportStatus, string> = {
  pending: "orange",
  processing: "blue",
  ready: "green",
  failed: "red",
  expired: "default",
};

const STATUS_OPTIONS: Array<"all" | TDataExportStatus> = [
  "all", "pending", "processing", "ready", "failed", "expired",
];

export default function DataExports() {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [companySearch, setCompanySearch] = useState<string>("");
  const [markReadyRecord, setMarkReadyRecord] = useState<TDataExport | null>(null);
  const [form] = Form.useForm();
  const [messageApi, ctx] = message.useMessage();

  const queryParams = {
    limit: 50,
    ...(statusFilter !== "all" ? { status: statusFilter } : {}),
    ...(companySearch.trim() ? { company_id: companySearch.trim() } : {}),
  };

  const { data, isLoading, isFetching, refetch } = useDataExportList(queryParams);
  const exports: TDataExport[] = data?.data ?? [];

  const { mutate: updateStatus, isPending: isUpdating } = useDataExportUpdateStatus({
    onSuccess: () => {
      messageApi.success("Export status updated.");
      setMarkReadyRecord(null);
      form.resetFields();
    },
    onError: (err: any) => messageApi.error(err?.message || "Failed to update status."),
  });

  const handleMarkFailed = (record: TDataExport) => {
    updateStatus({ id: record.id, status: "failed" });
  };

  const handleMarkReadySubmit = () => {
    form.validateFields().then((values) => {
      updateStatus({
        id: markReadyRecord!.id,
        status: "ready",
        file_url: values.file_url,
        expires_at: values.expires_at ? dayjs(values.expires_at).toISOString() : undefined,
      });
    });
  };

  const columns: ColumnsType<TDataExport> = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      width: 60,
      render: (val: number) => <span className="text-xs text-slate-400">#{val}</span>,
    },
    {
      title: "Company",
      dataIndex: "company_name",
      key: "company_name",
      render: (val: string | null) => (
        <span className="text-xs text-slate-700">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: "Requester",
      dataIndex: "requester_name",
      key: "requester_name",
      render: (val: string | null) => (
        <span className="text-xs text-slate-500">{val || <span className="italic text-slate-300">—</span>}</span>
      ),
    },
    {
      title: "Export Type",
      dataIndex: "export_type",
      key: "export_type",
      render: (val: string) => <Tag color="purple" className="font-mono text-xs">{val}</Tag>,
    },
    {
      title: "Format",
      dataIndex: "format",
      key: "format",
      render: (val: string) => <Tag color="default" className="font-mono text-xs">{val}</Tag>,
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (val: TDataExportStatus) => <Tag color={STATUS_COLOR[val]}>{val}</Tag>,
    },
    {
      title: "GDPR Reason",
      dataIndex: "gdpr_reason",
      key: "gdpr_reason",
      render: (val: string | null) =>
        val ? (
          <Tooltip title={val} overlayStyle={{ maxWidth: 360 }}>
            <span className="text-xs text-slate-500 cursor-help line-clamp-1 max-w-[140px] block">{val}</span>
          </Tooltip>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "File",
      dataIndex: "file_url",
      key: "file_url",
      render: (val: string | null) =>
        val ? (
          <a href={val} target="_blank" rel="noreferrer" className="text-xs text-violet-600 underline">
            Download
          </a>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "Expires At",
      dataIndex: "expires_at",
      key: "expires_at",
      render: (val: string | null) =>
        val ? (
          <span className="text-xs text-slate-400">{new Date(val).toLocaleDateString("en-GB")}</span>
        ) : (
          <span className="text-slate-300">—</span>
        ),
    },
    {
      title: "Requested",
      dataIndex: "created_at",
      key: "created_at",
      render: (val: string) => (
        <span className="text-xs text-slate-400">{new Date(val).toLocaleString("en-GB")}</span>
      ),
    },
    {
      title: "Actions",
      key: "actions",
      align: "right" as const,
      render: (_: any, record: TDataExport) => {
        const canAct = record.status === "pending" || record.status === "processing";
        if (!canAct) return <span className="text-xs text-slate-300 px-2">—</span>;
        return (
          <div className="flex gap-2 justify-end">
            <Button
              size="small"
              icon={<CheckCircle size={12} />}
              style={{ color: "#4c1d95", borderColor: "#4c1d95" }}
              onClick={() => {
                setMarkReadyRecord(record);
                form.resetFields();
              }}
            >
              Mark Ready
            </Button>
            <Popconfirm
              title="Mark as failed?"
              description="This export will be marked as failed. The requester will need to submit again."
              onConfirm={() => handleMarkFailed(record)}
              okText="Mark Failed"
              okButtonProps={{ danger: true }}
            >
              <Button size="small" danger icon={<XCircle size={12} />}>
                Mark Failed
              </Button>
            </Popconfirm>
          </div>
        );
      },
    },
  ];

  return (
    <div>
      {ctx}

      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-xl font-semibold text-slate-800">Data Exports</h2>
          <p className="text-slate-500 text-sm mt-0.5">
            GDPR Article 20 — manage company data export requests — showing last 50 records
          </p>
        </div>
        <Button
          icon={<RefreshCcw size={14} />}
          onClick={() => refetch()}
          loading={isFetching}
        >
          Refresh
        </Button>
      </div>

      {/* Filter bar */}
      <div className="flex items-center gap-3 mb-4 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Status:</span>
          <Select value={statusFilter} onChange={setStatusFilter} style={{ width: 160 }}>
            {STATUS_OPTIONS.map((s) => (
              <Option key={s} value={s}>{s === "all" ? "All Statuses" : s}</Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-slate-500">Company ID:</span>
          <Input
            placeholder="Search by company ID..."
            value={companySearch}
            onChange={(e) => setCompanySearch(e.target.value)}
            style={{ width: 180 }}
            allowClear
          />
        </div>
        {data?.extra && (
          <span className="text-xs text-slate-400 ml-2">{data.extra.total} total</span>
        )}
      </div>

      <Card className="rounded-2xl border border-slate-200 shadow-sm" styles={{ body: { padding: 0 } }}>
        {/* No client-side pagination — server returns max 50 records, use filters to narrow down */}
        <Table
          dataSource={exports}
          columns={columns}
          rowKey="id"
          pagination={false}
          loading={isLoading || isFetching}
          size="middle"
          scroll={{ x: 1200 }}
          locale={{ emptyText: "No data exports found for the selected filters." }}
          rowClassName={(record) =>
            record.status === "failed" ? "bg-red-50/40" : ""
          }
        />
      </Card>

      {/* Mark Ready Modal */}
      <Modal
        title={`Mark Export #${markReadyRecord?.id} as Ready`}
        open={!!markReadyRecord}
        onOk={handleMarkReadySubmit}
        onCancel={() => { setMarkReadyRecord(null); form.resetFields(); }}
        okText="Mark Ready"
        confirmLoading={isUpdating}
        okButtonProps={{ style: { background: "#4c1d95", borderColor: "#4c1d95" } }}
      >
        <Form form={form} layout="vertical" className="mt-4">
          <Form.Item
            name="file_url"
            label="File URL"
            rules={[{ required: true, message: "File URL is required" }, { type: "url", message: "Must be a valid URL" }]}
          >
            <Input placeholder="https://..." prefix={<Download size={14} />} />
          </Form.Item>
          <Form.Item name="expires_at" label="Expires At">
            <DatePicker style={{ width: "100%" }} showTime />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
